import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  Pagination, 
  Typography, 
  IconButton, 
  FormControl, 
  InputLabel, 
  Paper,
  Dialog, 
  DialogTitle, 
  DialogContent,
  DialogActions, 
  List, 
  ListItem, 
  ListItemText,
  Box,
  Stack,
  Collapse,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import loadingGif from '../../assets/loading.gif';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useWebSocket } from '../ws/WebsocketContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import API_BASE_URL from '../../config';
import './Order.css';

const formatEventDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  
  const month = utcDate.getMonth() + 1;
  const day = utcDate.getDate();
  const year = utcDate.getFullYear();
  
  let hours = utcDate.getHours();
  const minutes = utcDate.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};

const EventPanel = ({ date, events, onClose, onAddEvent, handleEventClick }) => {
  const navigate = useNavigate();
  return (
    <Paper 
      elevation={3} 
      style={{
        height: '40%',
        minHeight: '40%',
        maxHeight: '40%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        marginBottom: '10px'
      }}
    >
      {/* Fixed header */}
      <div 
        style={{ 
          height: 56,
          flexShrink: 0,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '0 16px',
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1,
        }}
      >
        <Typography variant="h6">{date.toDateString()}</Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={onAddEvent}
            size="small"
          >
            Add Event
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div style={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
        <List>
          {events.length > 0 ? (
            events.map((event) => (
              <ListItem 
                key={event.eventId} 
                divider 
                button
                onClick={() => handleEventClick(event)}
                style={{ cursor: 'pointer', alignItems: 'center' }}
              >
                <div 
                  style={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    backgroundColor: event.color || '#7d6c6c', 
                    marginRight: 12, 
                    flexShrink: 0 
                  }} 
                />
                <ListItemText 
                  primary={
                    event.relatedOrderId ? (
                      <span
                        style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          window.open(`/#/order/${event.relatedOrderId}`, '_blank');
                        }}
                      >
                        {event.title}
                      </span>
                    ) : (
                      event.title
                    )
                  }
                  secondary={`${event.eventType} | ${formatEventDateTime(event.start)} - ${formatEventDateTime(event.end)} | Note: ${event.description || 'N/A'}${event.eventType === 'Job Schedule' ? ` | ${event.orderPrefix}${event.orderName}: ${event.jobName}` : ''}`}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No events for this day." />
            </ListItem>
          )}
        </List>
      </div>
    </Paper>
  );
};

const CurrentOrder = () => {
  const navigate = useNavigate();
  const orgId = localStorage.getItem('selectedOrg');
  const token = localStorage.getItem('token');
  
  const [loading1, setLoading1] = useState(true);
  const [loading2, setLoading2] = useState(true);
  const [loading3, setLoading3] = useState(true);
  const [loading4, setLoading4] = useState(true);

  const [orderList, setOrderList] = useState([]);

  const [layoutMode, setLayoutMode] = useState('split'); // 'order', 'calendar', 'split'

  const [events, setEvents] = useState([]);
  
  const location = useLocation();
  const { redirectJobFlowId, redirectStepId } = location.state || {};

  const [jobFlows, setJobFlows] = useState([]);
  const [selectedJobFlow, setSelectedJobFlow] = useState(0);
  const [jobFlowSteps, setJobFlowSteps] = useState([]);
  const [jobFlowFullSteps, setJobFlowFullSteps] = useState([]);
  const [selectedJobFlowStep, setSelectedJobFlowStep] = useState(redirectStepId || '0');

  const [searchCriteria, setSearchCriteria] = useState('');
  const [urgentOnly, setUrgentOnly] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(-1);
  const [pageSize, setPageSize] = useState(10);
  const [pageOrders, setPageOrders] = useState([]);

  const [tag1Name, setTag1Name] = useState('tag1');
  const [tag2Name, setTag2Name] = useState('tag2');
  const [tag3Name, setTag3Name] = useState('tag3');
  const [tag4Name, setTag4Name] = useState('tag4');
  const [tag5Name, setTag5Name] = useState('tag5');
  const [tag6Name, setTag6Name] = useState('tag6');
  const [tag7Name, setTag7Name] = useState('tag7');
  const [tag8Name, setTag8Name] = useState('tag8');

  const [alertMsg, setAlertMsg] = useState('');

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);

  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventStart, setNewEventStart] = useState(null);
  const [newEventEnd, setNewEventEnd] = useState(null);
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventType, setNewEventType] = useState('');
  const [newEventColor, setNewEventColor] = useState('#007bff');
  const [newEventLocation, setNewEventLocation] = useState('');

  const [expandedOrders, setExpandedOrders] = useState({});

  const [flashingOrders, setFlashingOrders] = useState({}); 

  const [calendarEvents, setCalendarEvents] = useState([]); 

  const [deletePermission, setDeletePermission] = useState(false);
 
  const toggleExpand = (orderId, e) => {
    e.stopPropagation();
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const socket = useWebSocket();

  useEffect(() => {
    document.title = "Current Orders - IMS";
    
    return () => {
      document.title = "IMS";
    };
  }, []);

  useEffect(() => {
    setCalendarEvents(events.map(event => {
      // Convert UTC times from backend to local timezone
      const adjustedStart = new Date(event.start);
      adjustedStart.setHours(adjustedStart.getHours() - 10);
      
      const adjustedEnd = new Date(event.end);
      adjustedEnd.setHours(adjustedEnd.getHours() - 10);
      
      return {
        eventId: event.eventId,
        title: event.title,
        start: adjustedStart,
        end: adjustedEnd,
        description: event.description,
        location: event.location,
        eventType: event.eventType,
        color: event.color,
        relatedOrderId: event.relatedOrderId,
        jobName: event.jobName || 'N/A',
        orderPrefix: event.orderPrefix || 'N/A',
        orderName: event.orderName || 'N/A',
      };
    }));
  }, [events]);

  useEffect(() => {
    if (socket && orgId && selectedJobFlow) {
      const subscribeChannels = () => {
        try {
          socket.send(JSON.stringify({
            type: 'subscribe',
            channel: `jobflow:${selectedJobFlow}`
          }));
          socket.send(JSON.stringify({
            type: 'subscribe',
            channel: `current-order:${selectedJobFlow}`
          }));
        } catch (error) {
          console.error('WebSocket subscription error:', error);
        }
      };

      subscribeChannels();

      return () => {
        try {
          socket.send(JSON.stringify({
            type: 'unsubscribe',
            channel: `jobflow:${selectedJobFlow}`
          }));
          socket.send(JSON.stringify({
            type: 'unsubscribe',
            channel: `current-order:${selectedJobFlow}`
          }));
        } catch (error) {
          console.error('WebSocket unsubscription error:', error);
        }
      };
    }
  }, [socket, orgId, selectedJobFlow]);

  useEffect(() => {
    if (socket) {
      const handleMessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.channel === `jobflow:${selectedJobFlow}`) {
            toast.info(message.data || 'Default message');

            // Check if message.data contains order name to flash
            if (message.data) {
              // Extract order name from message.data
              const orderNameMatch = message.data.match(/:\s*(.+)$/);
              if (orderNameMatch) {
                const orderName = orderNameMatch[1].trim();

                // Find order(s) with matching prefix+order_name in pageOrders
                const matchingOrders = pageOrders.filter(order => 
                  `${order.prefix}${order.order_name}` === orderName
                );

                if (matchingOrders.length > 0) {
                  const newFlashing = { ...flashingOrders };
                  matchingOrders.forEach(order => {
                    newFlashing[order.order_id] = true;
                  });
                  fetchOrdersNoLoading(currentPage);
                  setFlashingOrders(newFlashing);

                  // Remove flashing after some time
                  setTimeout(() => {
                    setFlashingOrders(prev => {
                      const updated = { ...prev };
                      matchingOrders.forEach(order => {
                        delete updated[order.order_id];
                      });
                      return updated;
                    });
                  }, 10000);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socket.addEventListener('message', handleMessage);
      return () => socket.removeEventListener('message', handleMessage);
    }
  }, [socket, selectedJobFlow, pageOrders, flashingOrders]);

  const handleAddEvent = () => {
    const baseDate = selectedDate ? new Date(selectedDate) : new Date();

    const localStart = new Date(baseDate);
    localStart.setHours(7, 0, 0, 0);

    const localEnd = new Date(baseDate);
    localEnd.setHours(7, 0, 0, 0);

    // Convert local time to UTC (add timezone offset)
    const timezoneOffset = localStart.getTimezoneOffset();
    const utcStart = new Date(localStart.getTime() - (timezoneOffset * 60000));
    const utcEnd = new Date(localEnd.getTime() - (timezoneOffset * 60000));

    setNewEventTitle('');
    setNewEventStart(utcStart);
    setNewEventEnd(utcEnd);
    setNewEventDescription('');
    setNewEventType('Appointment');
    setNewEventColor('#007bff');
    setCreateEventDialogOpen(true);
  };

  const handleCreateEventSubmit = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/event/create`, {
        title: newEventTitle,
        description: newEventDescription,
        start_datetime: newEventStart,
        end_datetime: newEventEnd,
        allDay: false,
        location: newEventLocation,
        eventType: newEventType,
        category: '',
        recurrenceRule: '', 
        relatedOrderId: ''
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          selectedorg: orgId,
        },
      });

      if (response.status === 201) {
        // Refresh events or add new event locally
        setEvents(prev => [...prev, {
          eventId: response.data.event_id,
          title: newEventTitle,
          start: new Date(newEventStart),
          end: new Date(newEventEnd),
          description: newEventDescription,
          eventType: newEventType,
          color: newEventColor,
        }]);
        setCreateEventDialogOpen(false);
        toast.success('Event created successfully');
        window.location.reload();
      } else {
        toast.error('Failed to create event');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to create event');
    }
  };

const convertLocalDateTimeToUTC = (localDateTimeString) => { 
  const localDate = new Date(localDateTimeString);
  const timezoneOffset = localDate.getTimezoneOffset();
  return new Date(localDate.getTime() + (timezoneOffset * -1) * 60000);
};

const handleNewEventStartChange = (localDateTimeString) => { 
  const utcDate = convertLocalDateTimeToUTC(localDateTimeString);
  
  // Ensure end time is always after start time
  if (newEventEnd && newEventEnd <= utcDate) {
    // If start time is set after or equal to end time, adjust end time to be 1 hour later
    const newEndTime = new Date(utcDate.getTime() + (60 * 60 * 1000)); // Add 1 hour
    setNewEventEnd(newEndTime);
  }
  
  setNewEventStart(utcDate);
};

const handleNewEventEndChange = (localDateTimeString) => {
  const utcDate = convertLocalDateTimeToUTC(localDateTimeString);
  
  // Ensure end time is always after start time
  if (newEventStart && utcDate <= newEventStart) {
    // If end time is set before or equal to start time, adjust it to be 1 hour after start
    const newEndTime = new Date(newEventStart.getTime() + (60 * 60 * 1000)); // Add 1 hour
    setNewEventEnd(newEndTime);
  } else {
    setNewEventEnd(utcDate);
  }
};

const formatDateTimeForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const timezoneOffset = d.getTimezoneOffset();
  const adjustedDate = new Date(d.getTime());
  return adjustedDate.toISOString().slice(0, 16);
};

  const handleCreateEventCancel = () => {
    setCreateEventDialogOpen(false);
  };

  const handleDateClick = (arg) => {
    const clickedDate = arg.date;
    
    // Normalize to start of day in local timezone
    const clickedStartOfDayLocal = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate());
    const clickedEndOfDayLocal = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate() + 1);
    
    const clickedStartOfDay = new Date(clickedStartOfDayLocal.getTime() + (10 * 60 * 60 * 1000));
    const clickedEndOfDay = new Date(clickedEndOfDayLocal.getTime() + (10 * 60 * 60 * 1000));
  
    const eventsOnThisDay = events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Check if event overlaps with the clicked day
      return eventStart < clickedEndOfDay && eventEnd > clickedStartOfDay;
    });
    
    setSelectedDate(clickedDate);
    setSelectedDateEvents(eventsOnThisDay);
  };

  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [editEventData, setEditEventData] = useState(null);

  const handleEventClick = (event) => {
    setEditEventData(event);
    setEditEventDialogOpen(true);
  };

  const handleEditEventChange = (field, value) => {
    if (field === 'start' || field === 'end') {
      const localDate = new Date(value);
      
      const timezoneOffset = localDate.getTimezoneOffset();
      
      const utcDate = new Date(localDate.getTime() + (timezoneOffset * -1) * 60000);
      
      setEditEventData(prev => {
        const updatedData = { ...prev, [field]: utcDate };
        
        // Ensure end time is always after start time
        if (field === 'start' && updatedData.end && updatedData.end <= utcDate) {
          // If start time is set after or equal to end time, adjust end time to be 1 hour later
          const newEndTime = new Date(utcDate.getTime() + (60 * 60 * 1000)); // Add 1 hour
          updatedData.end = newEndTime;
        } else if (field === 'end' && updatedData.start && utcDate <= updatedData.start) {
          // If end time is set before or equal to start time, adjust it to be 1 hour after start
          const newEndTime = new Date(updatedData.start.getTime() + (60 * 60 * 1000)); // Add 1 hour
          updatedData.end = newEndTime;
        }
        
        return updatedData;
      });
    } else {
      setEditEventData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleEditEventSubmit = async () => {
    try {
      // Convert dates to MySQL compatible format
      const formatForMySQL = (date) => {
        if (!date) return null;
        const d = new Date(date);
        
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
      };

      const response = await axios.put(`${API_BASE_URL}/event/update/${editEventData.eventId}`, {
        title: editEventData.title,
        startDatetime: formatForMySQL(editEventData.start),
        endDatetime: formatForMySQL(editEventData.end),
        description: editEventData.description,
        eventType: editEventData.eventType,
        color: editEventData.color,
        location: editEventData.location || null,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          selectedorg: orgId,
        },
      });

      if (response.status === 200) {
        setEvents(prev => prev.map(ev => ev.eventId === editEventData.eventId ? editEventData : ev));
        setEditEventDialogOpen(false);
        toast.success('Event updated successfully');
        window.location.reload();
      } else {
        toast.error('Failed to update event');
      }
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  function formatLocalDateTimeForInput(date) {
    if (!date) return '';
    
    const d = new Date(date);
    
    const timezoneOffset = d.getTimezoneOffset();
    
    const adjustedDate = new Date(d.getTime() - (timezoneOffset * -1)  * 60000);
    
    const pad = (n) => n.toString().padStart(2, '0');
    
    return (
      adjustedDate.getFullYear() + '-' +
      pad(adjustedDate.getMonth() + 1) + '-' +
      pad(adjustedDate.getDate()) + 'T' +
      pad(adjustedDate.getHours()) + ':' +
      pad(adjustedDate.getMinutes())
    );
  }

  const handleEditEventDelete = (title) => {
    // make it z axis 1000
    setEditEventDialogOpen(false);
    Swal.fire({
      zIndex: 9999999,
      title: `Are you sure you want to delete event ${title}?`,
      text: 'This destructive action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        submitDeleteEvent();
      }
    });
  }

  const submitDeleteEvent = async () => {

    try {
      const response = await axios.delete(`${API_BASE_URL}/event/delete/${editEventData.eventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          selectedorg: orgId,
        },
      });
      if (response.status === 200) {
        setEvents(prev => prev.filter(ev => ev.eventId !== editEventData.eventId));
        setEditEventDialogOpen(false);
        toast.success('Event deleted successfully');
        window.location.reload();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  }

  const handleEditEventCancel = () => {
    setEditEventDialogOpen(false);
    setEditEventData(null);
  };

  const handleLayoutChange = (event, newLayout) => {
    if (newLayout !== null) {
      setLayoutMode(newLayout);
    }
  };
  
  const JobFlowStepper = ({ jobFlowSteps, currentStep }) => {
    const [hoveredStep, setHoveredStep] = useState(null);
    const prevStepRef = useRef(currentStep);
    const timeoutRef = useRef(null);

    const [animatingStep, setAnimatingStep] = useState(null);

    useEffect(() => {
      if (prevStepRef.current !== currentStep) {
        setAnimatingStep(currentStep);
        const timeout = setTimeout(() => setAnimatingStep(null), 600);
        prevStepRef.current = currentStep;
        return () => clearTimeout(timeout);
      }
    }, [currentStep]);

    const handleMouseLeave = () => {
      // timeoutRef.current = setTimeout(() => {
        setHoveredStep(null);
      // }, 450);
    };

    const handleMouseEnter = (stepNumber) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setHoveredStep(stepNumber);
    };

    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          userSelect: 'none',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'default',
          overflow: 'hidden',
          borderRadius: 0,
          boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)',
          backgroundColor: '#ddd',
        }}
        onMouseLeave={handleMouseLeave}
      >
        {jobFlowSteps.map((step, index) => {
          const isCurrent = step.step_number === currentStep;
          const isHovered = hoveredStep === step.step_number;
          const isAnimating = animatingStep === step.step_number;

          const expanded = isHovered || (!hoveredStep && isCurrent);
          const flexGrow = expanded ? 3 : 1;

          // Change here: previous steps have white background and black text
          const isPreviousStep = step.step_number < currentStep;
          const backgroundColor = isPreviousStep ? '#b1b1b1ff' : step.step_color;
          const color = '#fff';

          return (
            <div
              key={step.step_number}
              onMouseEnter={() => handleMouseEnter(step.step_number)}
              style={{
                backgroundColor,
                color,
                padding: '4px 10px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                flexGrow,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'default',
                userSelect: 'none',
                height: 28,
                marginLeft: index === 0 ? 0 : -8,
                borderRadius: 0,
                transform: isAnimating ? 'scale(1.05)' : 'none',
                zIndex: isAnimating ? 10 : 'auto',
                position: 'relative',
                backfaceVisibility: 'hidden',
                willChange: 'transform, flex-grow',
                pointerEvents: 'auto',
                boxShadow: isCurrent ? '0 0 0 2px white, inset 0 0 12px 4px white' : undefined,
                animation: isCurrent ? 'innerGlow 2s infinite' : undefined,
                overflow: 'visible',
              }}
            >
              {expanded ? step.step_name : step.step_name.charAt(0).toUpperCase()}
                {index !== jobFlowSteps.length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 0,
                      height: 0,
                      borderTop: '14px solid transparent',
                      borderBottom: '14px solid transparent',
                      borderLeft: `14px solid ${backgroundColor}`,
                      zIndex: 1,
                    }}
                  />
                )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!token) {
          navigate('/unauthorized');
          return;
      }

      setLoading3(true);

      try {
        const permissionsResponse = await axios.get(`${API_BASE_URL}/orders/current-order-permissions`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
        });

        if (!permissionsResponse.data[0].current_order_read) {
          navigate('/unauthorized');
          return;
        }

        setDeletePermission(permissionsResponse.data[0].current_order_delete);

        // Fetch job flows
        const jobFlowResponse = await axios.get(`${API_BASE_URL}/orders/job-flow`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
        });

        if (jobFlowResponse.status === 204 || !jobFlowResponse.data || jobFlowResponse.data.length === 0) {
          await Swal.fire({
            title: 'No Job Flow Found',
            text: 'Please create a job flow before creating an order.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
          navigate('/dashboard');
          return;
        }

        setJobFlows(jobFlowResponse.data);
        setSelectedJobFlow(redirectJobFlowId || jobFlowResponse.data.find((flow) => flow.default_job_flow === 1)?.id || "");

        // Fetch events
        const eventResponse = await axios.get(`${API_BASE_URL}/event/all-current-events`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
        });

        if (eventResponse.status === 204 || !eventResponse.data || eventResponse.data.length === 0) {
          await Swal.fire({
            title: 'Error Finding Events',
            text: 'Please contact system admin for more info.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
          navigate('/dashboard');
          return;
        }

        setEvents(eventResponse.data.events.map(event => ({
          eventId: event.event_id,
          title: event.title,
          start: new Date(event.start_datetime),
          end: new Date(event.end_datetime),
          description: event.description,
          location: event.location,
          eventType: event.event_type,
          color: event.color,
          relatedOrderId: event.related_order_id,
          jobName: event.job_name || 'N/A',
          orderPrefix: event.order_prefix || 'N/A',
          orderName: event.order_name || 'N/A',
        })));

      } catch (error) {
          console.error('Error fetching data:', error);
          navigate('/unauthorized');
          return;
      } finally {
          setLoading3(false);
      }

      // Fetch tags names
      const tagsResponse = await axios.get(`${API_BASE_URL}/orders/order-tags`, {
        headers: {
          Authorization: `Bearer ${token}`,
          selectedorg: orgId,
        },
      });

      if (!(tagsResponse.status === 204 || !tagsResponse.data || tagsResponse.data.length === 0)) {
        const tags = tagsResponse.data;

        tags.map(tag => {
          if (tag.tagIndex === 'tag1') setTag1Name(tag.tagName);
          if (tag.tagIndex === 'tag2') setTag2Name(tag.tagName);
          if (tag.tagIndex === 'tag3') setTag3Name(tag.tagName);
          if (tag.tagIndex === 'tag4') setTag4Name(tag.tagName);
          if (tag.tagIndex === 'tag5') setTag5Name(tag.tagName);
          if (tag.tagIndex === 'tag6') setTag6Name(tag.tagName);
          if (tag.tagIndex === 'tag7') setTag7Name(tag.tagName);
          if (tag.tagIndex === 'tag8') setTag8Name(tag.tagName);
        });
      };
    };

    fetchOrderDetails();
  }, [orgId, navigate]);  
  
  // Load job flow steps upon job flow change
  useEffect(() => {
    setLoading2(true);
  
    const fetchJobFlowSteps = async () => {
      if (selectedJobFlow) {

        try {
          const jobFlowStepsResponse = await axios.get(`${API_BASE_URL}/orders/steps`, {
            headers: {
              Authorization: `Bearer ${token}`,
              selectedorg: orgId,
            },
            params: {
              selectedJobFlow,
            },
          });

          if (jobFlowStepsResponse.status === 204 || !jobFlowStepsResponse.data) {
            await Swal.fire({
              title: 'Error Finding Job Flow Steps',
              text: 'Please contact system admin for more info.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
            navigate('/dashboard');
            return;
          }

          if (jobFlowStepsResponse.data.length !== 0) {
            // Transform steps to shift IDs to the previous step (except first step)
            const transformedSteps = jobFlowStepsResponse.data.slice(1).map((step, index) => {
              // Get the previous step's ID (from original array, index = current index + 1)
              const previousStep = jobFlowStepsResponse.data[index];
              return {
                ...step,
                id: previousStep.id, // Use previous step's ID
                step_number: step.step_number - 1, // Decrement step number
              };
            });
            setJobFlowSteps(transformedSteps);
          } else {
            setJobFlowSteps([]);
          }

          const jobFlowFullStepsResponse = await axios.get(`${API_BASE_URL}/orders/full-steps`, {
            headers: {
              Authorization: `Bearer ${token}`,
              selectedorg: orgId,
            },
            params: {
              selectedJobFlow,
            },
          });

          if (jobFlowFullStepsResponse.status === 204 || !jobFlowFullStepsResponse.data) {
            await Swal.fire({
              title: 'Error Finding Job Full Flow Steps',
              text: 'Please contact system admin for more info.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
            navigate('/dashboard');
            return;
          }

          if (jobFlowFullStepsResponse.data.length !== 0) {
            setJobFlowFullSteps(jobFlowFullStepsResponse.data);
          } else {
            setJobFlowFullSteps([]);
          }

        } catch (error) {
          console.error('Error fetching job flow step:', error);
          navigate('/unauthorized');
          return;
        } finally {
            setLoading2(false);
        }
      }
    };
    fetchJobFlowSteps();
  }, [selectedJobFlow]);  

  // Fetch Page Number when corrsponding parameters change
  // * searchCriteria is not included, fetchPageNumber is performed upon press of enter
  useEffect(() => {
    setOrderList([]);
    fetchPageNumber();
  }, [selectedJobFlow, selectedJobFlowStep, pageSize, urgentOnly, jobFlowSteps]);

  

  const fetchPageNumber = async () => {
    
    if (selectedJobFlow !== 0) {
      setLoading4(true);
    
      try {
        const step = jobFlowSteps.find((item) => item.id === selectedJobFlowStep);
        let stepNumber = '0';

        if (step && step.step_number != null) {
          stepNumber = step.step_number.toString();
        }

        console.log()
        
        const pagesResponse = await axios.get(`${API_BASE_URL}/orders/pages`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
          params: {
            selectedJobFlow,
            stepNumber,
            pageSize,
            searchCriteria,
            urgentOnly,
            selectedJobFlowStep
          },
        });
        
        if (pagesResponse.status === 204 || !pagesResponse.data) {
          await Swal.fire({
            title: 'Error Fetching Orders',
            text: 'Please contact system admin for more info.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
          navigate('/dashboard');
          return;
        }

        if (pagesResponse.data.length !== 0) {
          setOrderList(pagesResponse.data); 
          setTotalPages(Math.ceil(pagesResponse.data.length / pageSize));
          setCurrentPage(1);
        }

        // wait for orderList to be set
  
        // fetchOrders(1);
  
      } catch (error) {
        console.error('Error fetching pages:', error);
        navigate('/unauthorized');
        return;
      } finally {
          setLoading4(false);
      }
    }
  }

  // Load orders upon page change
  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage, orderList]);

  const fetchOrders = async (page) => {
    setLoading1(true);

    if (selectedJobFlow !== 0 && selectedJobFlowStep.length > 0) {
      try {
        // Calculate start and end index for pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        // Slice the order IDs for the current page
        let orderIdsForPage = [];
        if (orderList.length > 0) {
          orderIdsForPage = orderList.slice(startIndex, endIndex);
        }

        if (orderIdsForPage.length === 0) {
          setPageOrders([]);
          setAlertMsg('No Order Found ...');
          setLoading1(false);
          return;
        }

        const step = jobFlowSteps.find((item) => item.id === selectedJobFlowStep);
        let stepNumber = '0';

        if (step && step.step_number != null) {
          stepNumber = step.step_number.toString();
        }

        // Fetch orders filtered by order IDs for current page
        const pageOrdersResponse = await axios.get(`${API_BASE_URL}/orders/page-orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
          params: {
            selectedJobFlow,
            stepNumber,
            pageSize,
            searchCriteria,
            urgentOnly,
            page,
            orderIds: orderIdsForPage.join(',')
          },
        });

        if (pageOrdersResponse.status === 204 || !pageOrdersResponse.data) {
          await Swal.fire({
            title: 'Error Fetching Orders',
            text: 'Please contact system admin for more info.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
          navigate('/dashboard');
          return;
        }

        if (pageOrdersResponse.data.length !== 0) {
          setAlertMsg('');
          setPageOrders(pageOrdersResponse.data);
        } else {
          setAlertMsg('No Order Found ...');
          setPageOrders([]);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        navigate('/unauthorized');
        return;
      } finally {
        setLoading1(false);
      }
    }
  };

  const fetchOrdersNoLoading = async (page) => {

    if (selectedJobFlow !== 0 && selectedJobFlowStep.length > 0) {
      try {
        // Calculate start and end index for pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        // Slice the order IDs for the current page
        let orderIdsForPage = [];
        if (orderList.length > 0) {
          orderIdsForPage = orderList.slice(startIndex, endIndex);
        }

        if (orderIdsForPage.length === 0) {
          setPageOrders([]);
          setAlertMsg('No Order Found ...');
          setLoading1(false);
          return;
        }

        const step = jobFlowSteps.find((item) => item.id === selectedJobFlowStep);
        let stepNumber = '0';

        if (step && step.step_number != null) {
          stepNumber = step.step_number.toString();
        }

        // Fetch orders filtered by order IDs for current page
        const pageOrdersResponse = await axios.get(`${API_BASE_URL}/orders/page-orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
          params: {
            selectedJobFlow,
            stepNumber,
            pageSize,
            searchCriteria,
            urgentOnly,
            page,
            orderIds: orderIdsForPage.join(',')
          },
        });

        if (pageOrdersResponse.status === 204 || !pageOrdersResponse.data) {
          await Swal.fire({
            title: 'Error Fetching Orders',
            text: 'Please contact system admin for more info.',
            icon: 'warning',
            confirmButtonText: 'OK',
          });
          navigate('/dashboard');
          return;
        }

        if (pageOrdersResponse.data.length !== 0) {
          setAlertMsg('');
          setPageOrders(pageOrdersResponse.data);
        } else {
          setAlertMsg('No Order Found ...');
          setPageOrders([]);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        navigate('/unauthorized');
        return;
      }
    }
  };

  const handleUrgentOnlyChange = async () => {
    setUrgentOnly(prev => {
      const newUrgentOnly = !prev;
      setCurrentPage(1);

      return newUrgentOnly;
    });
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
  }

  const handleJobFlowChange = async (id) => {
    setOrderList([]);
    setCurrentPage(1);
    setSelectedJobFlow(id);
    setSelectedJobFlowStep('0');
  }

  const handleJobFlowStepChange = (id) => {
    setOrderList([]);
    setCurrentPage(1);
    setSelectedJobFlowStep(id);
  }

  const handleRefresh = async () => {
    await fetchPageNumber();
    fetchOrders(currentPage);
  }

  if (loading1 || loading2 || loading3 || loading4) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
      }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    );
  }

  // Sort events by color
  const sortedEvents = [...events].sort((a, b) => {
    if (a.color < b.color) return -1;
    if (a.color > b.color) return 1;
    return 0;
  });

  return (
    <>
     <div style={{ marginBottom: 0, display: 'flex', justifyContent: 'center' }}>
      <ToggleButtonGroup
        value={layoutMode}
        exclusive
        onChange={handleLayoutChange}
        aria-label="layout mode"
        size="medium"
        fullWidth
        sx={{
          backgroundColor: '#f9f9f9',
          display: 'flex',
          width: '100%',
          minWidth: '100%',
          maxWidth: 600,
          '& .MuiToggleButton-root': {
            border: 'none',
            borderBottom: '2px solid transparent',
            color: '#666',
            flex: 1,
            px: 0,
            py: 1,
            fontWeight: '600',
            textTransform: 'none',
            borderRadius: 0,
            justifyContent: 'center',
            transition: 'color 0.3s ease, border-bottom-color 0.3s ease',
            backgroundColor: 'transparent',
            '&:hover': {
              color: '#444',
              backgroundColor: 'transparent',
            },
          },
          '& .MuiToggleButton-root.Mui-selected': {
            color: '#222',
            borderBottomColor: '#1976d2',
            backgroundColor: 'transparent',
            fontWeight: '700',
          },
        }}
      >
        <ToggleButton value="order" aria-label="order only" title="Order Only">
          <ViewListIcon sx={{ mr: 1, color: 'inherit' }} />
          Order
        </ToggleButton>

        <ToggleButton value="split" aria-label="split view" title="Split View">
          <VerticalSplitIcon sx={{ mr: 1, color: 'inherit' }} />
          Split View
        </ToggleButton>

        <ToggleButton value="calendar" aria-label="calendar only" title="Calendar Only">
          <CalendarTodayIcon sx={{ mr: 1, color: 'inherit' }} />
          Calendar
        </ToggleButton>
      </ToggleButtonGroup>
    </div>

      <div 
        className="create-order-container" 
        style={{ 
          display: 'flex', 
          flexDirection: layoutMode === 'split' ? 'row' : 'column',
          gap: layoutMode === 'split' ? '0' : '-rem',
          height: 'calc(100vh - 110px)',
          overflowY: 'auto'
        }}
      >
         {(layoutMode === 'order' || layoutMode === 'split') && (
            <div 
              className="order-container" 
              style={{ 
                flex: layoutMode === 'split' ? 1 : 'unset', 
                width: layoutMode === 'split' ? '50%' : '100%',
                marginRight: 0,
                backgroundColor: '#ffffff'
              }}
            >
            <div className="selects-row">
              <div className="select-container">
                <label htmlFor="job-flow-select">Job Flow</label>
                <Select
                  id="job-flow-select"
                  labelId="job-flow-select-label"
                  value={selectedJobFlow || ""}
                  onChange={(e) => handleJobFlowChange(e.target.value)}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="" disabled>
                    Select a Job Flow
                  </MenuItem>
                  {jobFlows && jobFlows.length > 0 ? (
                    jobFlows.map((flow) => (
                      <MenuItem key={flow.id} value={flow.id}>
                        {flow.flow_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Job Flows Available</MenuItem>
                  )}
                </Select>
              </div>
              <div className="select-container">
                <label htmlFor="job-flow-step-select">Job Flow Step</label>
                <Select
                  id="job-flow-step-select"
                  labelId="job-flow-step-select-label"
                  value={selectedJobFlowStep || ""}
                  onChange={(e) => handleJobFlowStepChange(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={!selectedJobFlow}
                >
                  <MenuItem value="0">All</MenuItem> 
                  {jobFlowSteps && jobFlowSteps.length > 0 ? (
                    jobFlowSteps.map((step) => (
                      <MenuItem key={step.id} value={step.id}>
                        {step.step_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Job Flow Steps Available</MenuItem>
                  )}
                </Select>
              </div>
            </div>
            <div className="search-area">
            <TextField
              label="Filter Orders"
              placeholder="Enter keywords to filter orders"
              value={searchCriteria}
              onChange={(e) => {
                setSearchCriteria(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setSearchCriteria(searchCriteria);
                  fetchPageNumber();
                }
              }}
              multiline
              rows={0}
              inputProps={{ maxLength: 100 }}
            />
              <div className="buttons-container">
                <Button
                  variant="contained"
                  color="secondary"
                  className={`refresh-button`}
                  onClick={() => {
                    handleRefresh();
                    setFlashingOrders({});
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className={`urgent-order-button ${urgentOnly ? "active" : ""}`}
                  onClick={handleUrgentOnlyChange}
                >
                  Urgent Only
                </Button>
              </div>
            </div>
            <div className="separator"></div>
              <div className="order-items-container">
                {alertMsg && <p className="alert-message">{alertMsg}</p>}
                <div className="pagination-container">
                  <div className="page-size-selector">
                    <Select
                      id="page-size-select"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      displayEmpty
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                    <span>per page</span>
                  </div>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    shape="rounded"
                  />
                </div>
                {pageOrders
                  .filter((order, index, self) =>
                    index === self.findIndex((t) => t.order_id === order.order_id)
                  )
                  .map((order) => (
                    <div
                      key={order.order_id}
                      className={`job-card order-card ${flashingOrders[order.order_id] ? 'flashing' : ''}`}
                      onClick={() => window.open(`/#/order/${order.order_id}`, '_blank')}
                      style={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: order.jobs.some(job => job.urgent) ? '#ff7f00' : '#dee2e6',
                        backgroundColor: '#fafafa',
                        marginBottom: '25px',
                      }}
                    >
                      <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                          px: 1,
                          py: 0.5,
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          color: order.jobs.some(job => job.urgent) ? '#ff7f00' : '#000000',
                          borderRadius: 1,
                          flexDirection: 'column',
                        }}
                      >
                      {/* Top row: main header */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          userSelect: 'none',
                        }}
                      >
                        {/* Left section: order name and company/customer names */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            flexGrow: 1,
                            overflow: 'hidden',
                          }}
                          title={`${order.prefix} ${order.order_name}`}
                        >
                          <Typography
                            variant="h6"
                            noWrap
                            sx={{
                              color: 'inherit',
                              flexShrink: 0,
                              mr: 0,
                            }}
                          >
                            {order.prefix} {order.order_name}
                          </Typography>

                          {/* Dynamic vertical separator between order name and names */}
                          <Box
                            sx={{
                              borderLeft: '1px solid',
                              borderColor: '#dee2e6',
                              height: 24,
                              mx: 2,
                              flexShrink: 0,
                            }}
                          />

                          {/* Names container: max width, truncate if needed */}
                          <Stack
                            direction="column"
                            spacing={0.3}
                            sx={{
                              minWidth: 0,
                              maxWidth: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            {order.company ? (
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: '700', color: 'inherit' }}
                              >
                                {order.company.company_name}
                              </Typography>
                            ) : null}
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                fontWeight: order.company ? 'normal' : '700',
                                color: 'inherit',
                              }}
                            >
                              {order.customer.customer_name}
                            </Typography>
                          </Stack>
                        </Box>

                        

                        {/* Expand/Collapse Icon - click only toggles this order */}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(order.order_id, e);
                          }}
                          sx={{
                            color: 'text.primary',
                            p: 0.5,
                            ml: 1,
                            flexShrink: 0,
                          }}
                          aria-label={expandedOrders[order.order_id] ? 'Collapse details' : 'Expand details'}
                        >
                          { !!expandedOrders[order.order_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      {/* Expanded details section as part of header */}
                      <Collapse in={!!expandedOrders[order.order_id]} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 1 }}>
                        <Box sx={{
                            bgcolor: '#f0f0f0',
                            border: '1px solid #dee2e6',
                            borderRadius: '1px',
                            p: 1,
                            mb: 1,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                        }}>
                            <Typography 
                            color="text.primary"
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-line',
                                fontSize: '0.75rem'
                            }}>
                                {order.order_description || 'No order description'}
                            </Typography>
                        </Box>
                      </Collapse>
                    </Box>
                    {/* Tags 2x4 grid */}
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(80px, 1fr))',
                        gap: 0.5,
                        mr: 0,
                        flexShrink: 0,
                      }}
                    >
                      {[tag1Name, tag2Name, tag3Name, tag4Name, tag5Name, tag6Name, tag7Name, tag8Name].map((tagName, idx) => {
                        const tagKey = `tag${idx + 1}`;
                        const isActive = order[tagKey];
                        return (
                          <Typography
                            key={tagKey}
                            variant="caption"
                            sx={{
                              color: isActive ? 'success.light' : '#dee2e6',
                              borderRadius: 1,
                              px: 1,
                              py: 0.3,
                              textAlign: 'center',
                              fontSize: '0.65rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              userSelect: 'none',
                              minWidth: '80px',
                              border: '1px solid',
                              borderColor: isActive ? 'success.light' : '#dee2e6',
                            }}
                          >
                            {tagName}
                          </Typography>
                        );
                      })}
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    
                    <div className="order-jobs" style={{ marginTop: 12 }}>
                      {order.jobs && order.jobs.length > 0 ? (
                        <ul className="job-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {order.jobs.map((job) => (
                            <li
                              key={job.job_id}
                              onClick={() => window.open(`/#/order/${order.order_id}`, '_blank')}
                              className={`job-item ${job.urgent ? 'urgent' : ''}`}
                              style={{
                                position: 'relative',
                                cursor: 'pointer',
                                padding: '16px 16px 12px 16px',
                                marginBottom: 20,
                                marginTop: 8,
                                borderRadius: 6,
                                backgroundColor: '#f7f7f7',
                                borderColor: '#ccc',
                                borderWidth: 1,
                                borderStyle: 'solid',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                userSelect: 'none',
                                transition: 'background-color 0.3s ease',
                              }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eeeeee'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f7f7f7'}
                            >
                              <span style={{
                                position: 'absolute',
                                top: -12,
                                left: 16,
                                border: job.urgent ? '1px solid #ff7f00' : '1px solid #ccc',
                                backgroundColor: '#ffffff',
                                padding: '2px 12px',
                                borderRadius: '6px',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.07)',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                              }}>
                                {job.job_name}
                              </span>
                              <JobFlowStepper jobFlowSteps={jobFlowFullSteps} currentStep={job.current_step + 1} />
                              {/* Expanded details section as part of header */}
                                <Collapse in={!!expandedOrders[order.order_id]} timeout="auto" unmountOnExit sx={{ width: '100%', mt: 1 }}>
                                  <Box sx={{
                                      bgcolor: '#f0f0f0',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '1px',
                                      p: 1,
                                      mb: 1,
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                  }}>
                                      <Typography 
                                      color="text.primary" 
                                      sx={{ 
                                          whiteSpace: 'pre-line',
                                          fontSize: '0.75rem'
                                      }}>
                                          {job.description || 'No job description'}
                                      </Typography>
                                      
                                      {/* Product information section */}
                                      {job.products && job.products.length > 0 && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                                          <Typography 
                                              color="text.secondary" 
                                              sx={{ 
                                                  fontSize: '0.7rem',
                                                  fontWeight: 'bold',
                                                  mb: 0.5
                                              }}
                                          >
                                              Products:
                                          </Typography>
                                          <ul style={{
                                              margin: 0,
                                              paddingLeft: 0,
                                              listStyle: 'none',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: '4px',
                                          }}>
                                              {job.products
                                                  .filter(product => product.quantity > 0)
                                                  .map(product => (
                                                  <li 
                                                      key={product.job_product_id}
                                                      style={{
                                                          display: 'flex',
                                                          justifyContent: 'space-between',
                                                          alignItems: 'center',
                                                          padding: '4px 8px',
                                                          backgroundColor: '#e8e8e8',
                                                          borderRadius: '4px',
                                                          fontSize: '0.7rem',
                                                      }}
                                                  >
                                                      <span style={{
                                                          flexShrink: 1,
                                                          whiteSpace: 'nowrap',
                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                          marginRight: '8px',
                                                      }}
                                                          title={product.item_name}
                                                      >
                                                          {product.item_name}
                                                      </span>
                                                      <span style={{
                                                          whiteSpace: 'nowrap',
                                                          flexShrink: 0,
                                                          fontWeight: '600',
                                                          textAlign: 'right',
                                                          minWidth: '50px',
                                                      }}>
                                                          {product.quantity} {product.unit_symbol || ''}
                                                      </span>
                                                  </li>
                                              ))}
                                          </ul>
                                      </Box>
                                      )}
                                  </Box>
                                </Collapse>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="no-jobs" style={{ fontStyle: 'italic', color: '#888', marginTop: 8 }}>
                          No jobs associated with this order.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="pagination-container">
                  <div className="page-size-selector">
                    <Select
                      id="page-size-select"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      displayEmpty
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                    <span>per page</span>
                  </div>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    shape="rounded"
                  />
                </div>
              </div>
            </div>
          )}
          {(layoutMode === 'calendar' || layoutMode === 'split') && (
            <>
              <style>
                {`
                  .calendar-container {
                    position: relative;
                    transition: opacity 0.5s ease;
                  }
                  .calendar-container.dimmed {
                    opacity: 0.3;
                    pointer-events: none;
                  }
                  .event-list-overlay {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    right: 10px;
                    bottom: 10px;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                    border-radius: 8px;
                    padding: 20px;
                    overflow-y: auto;
                    opacity: 0;
                    animation: fadeIn 0.5s forwards;
                    z-index: 2000;
                  }
                  @keyframes fadeIn {
                    to {
                      opacity: 1;
                    }
                  }
                  .event-list-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                  }
                  .event-list-close-btn {
                    cursor: pointer;
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    font-weight: bold;
                    line-height: 1;
                  }
                  .event-item {
                    padding: 10px 0;
                    border-bottom: 1px solid #ddd;
                    cursor: pointer;
                  }
                  .event-item:last-child {
                    border-bottom: none;
                  }
                  .event-item:hover {
                    background-color: #f0f0f0;
                  }
                `}
              </style>

              <div
                className={`calendar-container`}
                style={{
                  flex: layoutMode === 'split' ? 1 : 'unset',
                  width: layoutMode === 'split' ? '50%' : '100%',
                  marginLeft: 0,
                  marginTop: layoutMode === 'split' ? 0 : 0,
                  height: layoutMode === 'split' ? 'auto' : 'calc(100vh - 105px)',
                  backgroundColor: '#fff',
                  padding: '10px',
                  boxSizing: 'border-box',
                  borderRadius: 0,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {selectedDate && (
                  <EventPanel 
                    date={selectedDate} 
                    events={selectedDateEvents} 
                    onClose={() => setSelectedDate(null)} 
                    onAddEvent={handleAddEvent}
                    handleEventClick={handleEventClick}
                  />
                )}
                <FullCalendar 
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} 
                  initialView="dayGridMonth" 
                  headerToolbar={{ 
                    left: 'prev,today,next', 
                    center: 'title', 
                    right: 'dayGridMonth,timeGridWeek,timeGridDay', 
                  }} 
                  events={calendarEvents} 
                  selectable={true} 
                  dateClick={handleDateClick} 
                  height="100%" 
                  nowIndicator={true} 
                  eventTimeFormat={{ 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: false, 
                  }} 
                  eventClick={(info) => { 
                    handleDateClick({ date:  info.event.start }); 
                  }}
                />
              </div>
            </>
          )}
      </div>
      
      <Dialog open={createEventDialogOpen} onClose={handleCreateEventCancel} maxWidth="sm" fullWidth>
        <DialogTitle style={{ color: 'black', whiteSpace: 'normal' }}>
          Create New Event
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={newEventTitle}
            onChange={(e) => setNewEventTitle(e.target.value)}
            multiline
            rows={1}
            required
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={1}
            value={newEventDescription}
            onChange={(e) => setNewEventDescription(e.target.value)}
          />
          <TextField
            label="Start Time"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={newEventStart ? formatDateTimeForInput(newEventStart) : ''}
            onChange={(e) => handleNewEventStartChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Time"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={newEventEnd ? formatDateTimeForInput(newEventEnd) : ''}
            onChange={(e) => handleNewEventEndChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Location"
            fullWidth
            multiline
            row={1}
            margin="normal"
            value={newEventLocation || ''}
            onChange={(e) => setNewEventLocation(e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="event-type-label">Event Type</InputLabel>
            <Select
              labelId="event-type-label"
              value={newEventType}
              label="Event Type"
              onChange={(e) => setNewEventType(e.target.value)}
            >
              <MenuItem value="Appointment">Appointment</MenuItem>
              <MenuItem value="Task">Task</MenuItem>
              <MenuItem value="Leave">Leave</MenuItem>
              <MenuItem value="Reminder">Reminder</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateEventCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateEventSubmit} disabled={!newEventTitle || !newEventStart || !newEventEnd}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} limit={5} />
      <Dialog open={editEventDialogOpen} onClose={handleEditEventCancel} maxWidth="sm" fullWidth>
        <DialogTitle style={{ color: 'black', whiteSpace: 'normal' }}>Edit Event</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            multiline
            rows={1}
            margin="normal"
            value={editEventData?.title || ''}
            onChange={(e) => handleEditEventChange('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={0}
            value={editEventData?.description || ''}
            maxLength={1000}
            onChange={(e) => handleEditEventChange('description', e.target.value)}
          />
          <TextField
            label="Start"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={editEventData?.start ? formatLocalDateTimeForInput(editEventData.start) : ''}
            onChange={(e) => handleEditEventChange('start', new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={editEventData?.end ? formatLocalDateTimeForInput(editEventData.end) : ''}
            onChange={(e) => handleEditEventChange('end', new Date(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Location"
            fullWidth
            multiline
            rows={1}
            margin="normal"
            value={editEventData?.location || ''}
            maxLength={255}
            onChange={(e) => handleEditEventChange('location', e.target.value)}
          />
          <Select
            label="Event Type"
            fullWidth
            margin="normal"
            value={editEventData?.eventType || ''}
            onChange={(e) => handleEditEventChange('eventType', e.target.value)}
            disabled={editEventData?.eventType === 'Job Schedule'}
            inputProps={{ 'aria-label': 'Event Type' }}
            style={{ marginTop: 16, marginBottom: 8 }}
          >
            {['Appointment', 'Task', 'Leave', 'Reminder', 'Other'].map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
            {editEventData?.eventType === 'Job Schedule' && (
              <MenuItem value="Job Schedule" disabled>
                Job Schedule
              </MenuItem>
            )}
          </Select>
          {editEventData?.eventType === 'Job Schedule' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Associated with:
              </Typography>
              <Typography variant="body2">
                <strong>Order:</strong> {editEventData?.orderPrefix}{editEventData?.orderName || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Job:</strong> {editEventData?.jobName || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditEventCancel}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={() => handleEditEventDelete(editEventData?.title || '')}
            disabled={!deletePermission}
            sx={{ marginLeft: 1 }}
          >
            Delete
          </Button>
          <Button variant="contained" color="primary" onClick={handleEditEventSubmit}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CurrentOrder;
